/**
 * The Athios Score, levels and setup quests shown during onboarding and on the dashboard, and
 * the level brands see on an athlete's profile.
 *
 * The score is 100 points in four slices (Creator Lab Academy founder brief, October 2026):
 *
 * - Profile, 30: photo, bio, position, connected accounts and the other setup quests
 * - Activity, 25: organic posting over a rolling 30 days (engagement joins when it's measured)
 * - Platform record, 15: deals finished, work on time
 * - Academy, 30: a lesson watched through, then its action taken (the lesson's action, academy.ts)
 *
 * An action pays into its home slice whenever it's done; the Academy half pays once, when the
 * lesson is watched AND the action is done, in either order, so nothing pays twice and nobody
 * redoes anything. Skip every lesson and the most you can reach is 70. Going quiet drains
 * Activity only, and a level once earned is kept (`level_earned`, saved by the server). Paid
 * deals still gate the higher levels.
 */

import { ACADEMY_MAX_POINTS, type LessonAction, type LessonActionId } from './academy'

export type Quest = {
  id:
    | 'profile'
    | 'themes'
    | 'socials'
    | 'rate_card'
    | 'app'
    | 'notifications'
    | 'phone_bank'
    | 'address'
    | 'publish'
  title: string
  subtitle: string
  /** Points shown next to the quest, e.g. "+3" or "+15" */
  pointsLabel: string
  points: number
  done: boolean
  /** Where tapping the quest takes the athlete */
  to: string
}

export type Level = 'L1' | 'L2' | 'L3' | 'L4'

/** The four slices of the 100-point score */
export const SCORE_SLICES = { profile: 30, activity: 25, record: 15, academy: ACADEMY_MAX_POINTS }
export type ScoreSlice = keyof typeof SCORE_SLICES

export const LEVELS: { level: Level; minScore: number; minDeals: number }[] = [
  { level: 'L1', minScore: 0, minDeals: 0 },
  { level: 'L2', minScore: 20, minDeals: 0 }, // = ONBOARDING_SCORE
  { level: 'L3', minScore: 60, minDeals: 3 },
  { level: 'L4', minScore: 85, minDeals: 10 },
]

/** The score (with a complete profile) that finishes onboarding and unlocks the full app */
export const ONBOARDING_SCORE = 20

/**
 * The lowest an athlete's score goes once they have finished onboarding (reached L2). Activity
 * drains when they stop posting and late deals cost Record points, but an established athlete
 * never falls back toward zero. New athletes see their real score while they set up.
 */
export const SCORE_FLOOR = 30

/** What connecting every platform adds to the Profile slice (half of it, by design) */
export const SOCIALS_MAX_POINTS = 15

export type AthleteLike = {
  avatar?: string | null
  bio?: string | null
  sports?: number[]
  school?: { id: string; name: string } | null
  socialAccounts?: { platform: string; oauth_connected?: boolean }[]
  pendingSocialHandles?: { platform: string; handle: string }[]
  phone_verified?: boolean
  paymentMethods?: unknown[]
  mailing_address?: unknown
  public_profile_published?: boolean
  app_installed_at?: string | null
  push_enabled?: boolean
  created_at?: string
  onboarding_active_days?: number | null
  rate_card?: unknown
  /** Their top content themes, ranked (up to THEMES_GOAL) */
  content_themes?: unknown[] | null
  /** Organic posting, from the content calendar (computed by the server) */
  posting?: PostingSummary | null
  /** Creator Academy lessons watched through (computed by the server) */
  academy?: {
    watched: number
    lessons?: string[]
    /** The published lessons that carry points, by lesson id (lessonActions in academy.ts) */
    actions?: Record<string, LessonAction>
  } | null
  /** Deals, for the Platform record slice (computed by the server) */
  record?: DealRecord | null
  /** The highest level the athlete has reached; levels are kept once earned */
  level_earned?: Level | null
}

export type DealRecord = {
  /** Offers the athlete accepted (including ones since finished) */
  acceptedDeals: number
  completedDeals: number
  /** Accepted deals with a step past its due date */
  overdueDeals: number
}

/** How many content themes finish the themes quest (the most an athlete can list) */
export const THEMES_GOAL = 5

/** The athlete's ranked content themes (older profiles have them only on the rate card) */
export const themesOf = (athlete: AthleteLike): { id: string; label: string }[] => {
  const list = athlete.content_themes ?? (athlete.rate_card as any)?.themes
  return Array.isArray(list) ? list.filter((t: any) => t?.id && t?.label) : []
}

// ── Activity: organic posting over a rolling 30 days ─────────────────────────

/** Organic posts a week (Monday to Sunday), the goal the calendar plans around */
export const POSTING_GOAL = 2
/** Posts in the last 30 days that fill the Activity slice (two a week) */
export const ACTIVITY_POSTS_GOAL = 8
/** How many finished weeks the calendar summary lists */
export const POSTING_WEEKS = 4

export type PostingSummary = {
  goal: number
  /** Posted so far this week */
  thisWeek: number
  /** Monday of this week, YYYY-MM-DD */
  weekStart: string
  /** The last finished weeks since the athlete joined, newest first */
  weeks: { weekStart: string; posted: number }[]
  /** Posted in the last 30 days, the window the Activity slice reads */
  last30?: number
  /** Posted ever, and planned for today or later */
  postedTotal?: number
  plannedAhead?: number
}

/** The Activity slice: posts in the last 30 days against ACTIVITY_POSTS_GOAL, up to 25 */
export function activityPoints(posting?: PostingSummary | null) {
  const posts = posting?.last30 ?? 0
  return Math.round(
    (SCORE_SLICES.activity * Math.min(posts, ACTIVITY_POSTS_GOAL)) / ACTIVITY_POSTS_GOAL,
  )
}
/** Kept for older callers: posting's part of the score is now the Activity slice */
export const postingPoints = activityPoints

// ── Platform record ───────────────────────────────────────────────────────────

/** Each finished deal adds 5 (up to 15); each overdue deal takes 5 back until it's done */
export const RECORD_POINTS_PER_DEAL = 5

export function recordPoints(record?: Partial<DealRecord> | null) {
  const done = record?.completedDeals ?? 0
  const late = record?.overdueDeals ?? 0
  return Math.max(0, Math.min(SCORE_SLICES.record, (done - late) * RECORD_POINTS_PER_DEAL))
}

/** The four pieces of the profile quest, each with whether it is done */
export const profilePieces = (athlete: AthleteLike) => [
  { id: 'photo', label: 'Photo', done: !!athlete.avatar },
  { id: 'bio', label: 'Bio', done: !!athlete.bio },
  { id: 'sport', label: 'Sport', done: (athlete.sports?.length ?? 0) > 0 },
  { id: 'school', label: 'School', done: !!athlete.school },
]
export const profileComplete = (athlete: AthleteLike) =>
  profilePieces(athlete).every((p) => p.done)

export const connectedPlatforms = (athlete: AthleteLike) =>
  (athlete.socialAccounts ?? []).filter((a) => a.oauth_connected).map((a) => a.platform)

/** Platforms the athlete can connect today */
export const CONNECTABLE_PLATFORMS = ['instagram', 'tiktok', 'youtube']

/**
 * The accounts we know the athlete has: ones linked from public data or handles given at
 * signup (not yet connected), plus anything already connected. Connecting all of them is worth
 * SOCIALS_MAX_POINTS in total, split evenly, so with one known account it earns 15, with two the first earns 8 (7.5 rounded) and both earn 15.
 * An athlete we know nothing about earns the full 15 on their first connection.
 */
export function knownPlatforms(athlete: AthleteLike) {
  const known = new Set<string>([
    ...(athlete.socialAccounts ?? []).map((a) => a.platform),
    ...(athlete.pendingSocialHandles ?? []).map((h) => h.platform),
  ])
  return [...known].filter((p) => CONNECTABLE_PLATFORMS.includes(p))
}

export function socialPoints(athlete: AthleteLike) {
  const connected = connectedPlatforms(athlete).filter((p) => CONNECTABLE_PLATFORMS.includes(p))
  const total = Math.max(1, knownPlatforms(athlete).length)
  return {
    connected: connected.length,
    total,
    earned: Math.round((SOCIALS_MAX_POINTS * Math.min(connected.length, total)) / total),
    done: connected.length > 0 && connected.length >= total,
  }
}

/** What connecting this platform would add to the score right now (0 if it adds nothing) */
export function pointsForConnecting(athlete: AthleteLike, platform: string) {
  if (connectedPlatforms(athlete).includes(platform)) return 0
  const before = socialPoints(athlete).earned
  const after = socialPoints({
    ...athlete,
    socialAccounts: [
      ...(athlete.socialAccounts ?? []).filter((a) => a.platform !== platform),
      { platform, oauth_connected: true },
    ],
  }).earned
  return Math.max(0, after - before)
}

export function buildQuests(athlete: AthleteLike, extras: { profilePublished?: boolean } = {}) {
  const social = socialPoints(athlete)
  const quests: Quest[] = [
    {
      id: 'profile',
      title: 'Complete your profile',
      subtitle: 'Photo, bio, sport and school',
      pointsLabel: '+6',
      points: 6,
      done: profileComplete(athlete),
      to: '/setup/about',
    },
    {
      id: 'themes',
      title: `Add your top ${THEMES_GOAL} content themes`,
      subtitle:
        themesOf(athlete).length && themesOf(athlete).length < THEMES_GOAL
          ? `${themesOf(athlete).length} of ${THEMES_GOAL} picked`
          : 'What you post again and again',
      pointsLabel: '+2',
      points: 2,
      done: themesOf(athlete).length >= THEMES_GOAL,
      to: '/setup/themes',
    },
    {
      id: 'socials',
      title: 'Connect your socials',
      subtitle:
        social.total > 1 && !social.done
          ? `${social.connected} of ${social.total} connected`
          : 'Real score and a higher price',
      pointsLabel: `+${SOCIALS_MAX_POINTS}`,
      points: social.earned,
      done: social.done,
      to: '/setup/connect',
    },
    {
      id: 'app',
      title: 'Add Athios to your home screen',
      subtitle: 'Open it like an app',
      pointsLabel: '+1',
      points: 1,
      done: !!athlete.app_installed_at,
      to: '/setup/app',
    },
    {
      id: 'notifications',
      title: 'Turn on notifications',
      subtitle: 'Hear about offers first',
      pointsLabel: '+1',
      points: 1,
      done: !!athlete.push_enabled,
      to: '/setup/app',
    },
    {
      id: 'rate_card',
      title: 'Set your rate card',
      subtitle: 'Your prices and ad licensing',
      pointsLabel: '+2',
      points: 2,
      done: !!athlete.rate_card,
      to: '/setup/rates',
    },
    {
      id: 'phone_bank',
      title: 'Confirm phone and add bank',
      subtitle: 'Payouts turn on',
      pointsLabel: '+1',
      points: 1,
      done: !!athlete.phone_verified && (athlete.paymentMethods?.length ?? 0) > 0,
      to: '/setup/payout',
    },
    {
      id: 'address',
      title: 'Add your mailing address',
      subtitle: 'Gifting offers turn on',
      pointsLabel: '+1',
      points: 1,
      done: !!athlete.mailing_address,
      to: '/setup/payout',
    },
    {
      id: 'publish',
      title: 'Open your Content Shop',
      subtitle: 'Brands can book your content',
      pointsLabel: '+1',
      points: 1,
      done: !!(extras.profilePublished ?? athlete.public_profile_published),
      to: '/setup/publish',
    },
  ]
  return quests
}

/** The Profile slice: the setup quests, which add up to 30 */
export function computeScore(quests: Quest[]) {
  const earned = quests.reduce((sum, q) => sum + (q.done ? q.points : 0), 0)
  return Math.min(SCORE_SLICES.profile, earned)
}

// ── Academy: each lesson's action, once watched ───────────────────────────────

type ActionContext = { athlete: AthleteLike; record: DealRecord; profilePublished: boolean }

const ACTION_DONE: Record<LessonActionId, (c: ActionContext) => boolean> = {
  profile: ({ athlete }) => profileComplete(athlete),
  themes: ({ athlete }) => themesOf(athlete).length >= THEMES_GOAL,
  first_post: ({ athlete }) => (athlete.posting?.postedTotal ?? 0) > 0,
  payouts: ({ athlete }) => !!athlete.phone_verified && (athlete.paymentMethods?.length ?? 0) > 0,
  plan_post: ({ athlete }) =>
    (athlete.posting?.plannedAhead ?? 0) > 0 || (athlete.posting?.postedTotal ?? 0) > 0,
  socials: ({ athlete }) => socialPoints(athlete).done,
  publish: ({ profilePublished }) => profilePublished,
  posting_goal: ({ athlete }) => (athlete.posting?.last30 ?? 0) >= ACTIVITY_POSTS_GOAL,
  rate_card: ({ athlete }) => !!athlete.rate_card,
  first_offer: ({ record }) => record.acceptedDeals > 0,
  first_deal: ({ record }) => record.completedDeals >= 1,
  three_deals: ({ record }) => record.completedDeals >= 3,
}

export type LessonCredit = {
  lessonId: string
  label: string
  to: string
  points: number
  watched: boolean
  actionDone: boolean
  /** Both halves are in: watched and acted */
  earned: boolean
}

/** Every lesson that carries points, with whether it's watched, acted on, and paid */
export function lessonCredits(c: ActionContext): LessonCredit[] {
  const watched = new Set(c.athlete.academy?.lessons ?? [])
  return Object.entries(c.athlete.academy?.actions ?? {}).map(([lessonId, a]) => {
    const actionDone = ACTION_DONE[a.action](c)
    return {
      lessonId,
      label: a.label,
      to: a.to,
      points: a.points,
      watched: watched.has(lessonId),
      actionDone,
      earned: actionDone && watched.has(lessonId),
    }
  })
}

const levelRank = (l?: Level | null) => (l ? LEVELS.findIndex((x) => x.level === l) : -1)

export function profileCompletion(quests: Quest[]) {
  const done = quests.filter((q) => q.done).length
  return Math.round((done / quests.length) * 100)
}

export function levelFor(score: number, completedDeals: number, profileComplete: boolean): Level {
  let level: Level = 'L1'
  for (const l of LEVELS) {
    const meetsProfile = l.level === 'L1' || profileComplete
    if (score >= l.minScore && completedDeals >= l.minDeals && meetsProfile) level = l.level
  }
  return level
}

export const nextLevel = (level: Level): Level | null => {
  const i = LEVELS.findIndex((l) => l.level === level)
  return LEVELS[i + 1]?.level ?? null
}

/**
 * Which day of the athlete's first week it is (1-7): the number of separate days they have come
 * back and used the app during onboarding (recorded by the server), not days since signup.
 */
export function dayOfFirstWeek(activeDays?: number | null) {
  return Math.min(7, Math.max(1, activeDays || 1))
}

export function athleteProgress(
  athlete: AthleteLike,
  options: { completedDeals?: number; overdueDeals?: number; profilePublished?: boolean } = {},
) {
  const quests = buildQuests(athlete, { profilePublished: options.profilePublished })
  const profilePublished = !!(options.profilePublished ?? athlete.public_profile_published)
  const record: DealRecord = {
    acceptedDeals: Math.max(
      athlete.record?.acceptedDeals ?? 0,
      options.completedDeals ?? athlete.record?.completedDeals ?? 0,
    ),
    completedDeals: options.completedDeals ?? athlete.record?.completedDeals ?? 0,
    overdueDeals: options.overdueDeals ?? athlete.record?.overdueDeals ?? 0,
  }
  const credits = lessonCredits({ athlete, record, profilePublished })
  const slices = {
    profile: computeScore(quests),
    activity: activityPoints(athlete.posting),
    record: recordPoints(record),
    academy: Math.min(
      SCORE_SLICES.academy,
      credits.reduce((sum, c) => sum + (c.earned ? c.points : 0), 0),
    ),
  }
  const rawScore = Math.min(100, slices.profile + slices.activity + slices.record + slices.academy)
  // Past onboarding, the score doesn't drop below the floor
  const established =
    levelRank(athlete.level_earned) >= levelRank('L2') ||
    (rawScore >= ONBOARDING_SCORE && profileComplete(athlete))
  const score = established ? Math.max(SCORE_FLOOR, rawScore) : rawScore
  // Kept for screens written before the slices: posting's and the Academy's parts
  const consistency = slices.activity
  const learning = slices.academy
  const completion = profileCompletion(quests)
  const deals = record.completedDeals
  // "Complete profile" for levelling means the core profile (photo, bio, sport, school). The
  // other quests add points but don't hold an athlete back.
  const profileDone = profileComplete(athlete)
  // A level once earned stays, even if Activity drains later
  const reached = levelFor(score, deals, profileDone)
  const level =
    levelRank(athlete.level_earned) > levelRank(reached) ? athlete.level_earned! : reached
  const next = nextLevel(level)
  const nextRequirement = next ? LEVELS.find((l) => l.level === next)! : null
  return {
    quests,
    score,
    /** The slices added up, before the floor */
    rawScore,
    /** The floor is holding the score up (the slices add up to less) */
    floored: score > rawScore,
    completion,
    level,
    next,
    nextRequirement,
    nextQuest: quests.find((q) => !q.done) ?? null,
    scoreReadyForNext: nextRequirement ? score >= nextRequirement.minScore : true,
    completedDeals: deals,
    profileDone,
    pieces: profilePieces(athlete),
    /** Onboarding is finished at L2: a score of 40 and a complete profile */
    graduated: score >= ONBOARDING_SCORE && profileDone,
    /** Day 1-7 of the athlete's first week, for the encouragement under the headings */
    day: dayOfFirstWeek(athlete.onboarding_active_days),
    /** The four slices: Profile (30), Activity (25), Platform record (15), Academy (30) */
    slices,
    /** Lessons that carry points, and the next one to watch or act on */
    lessons: credits,
    nextLesson: credits.find((c) => !c.earned) ?? null,
    record,
    /** The Activity slice (posting over 30 days), under its older name */
    consistency,
    posting: athlete.posting ?? null,
    /** The Academy slice (0 to ACADEMY_MAX_POINTS), under its older name */
    learning,
    lessonsWatched: athlete.academy?.watched ?? 0,
  }
}

// ── Deals, from the athlete's real collaborations ─────────────────────────────

type CollabLike = {
  status: string
  paymentStatus?: string
  endDate?: string
  contentDueDate?: string
  milestones?: { slug?: string; type_slug?: string; status: string; dueDate?: string }[]
}

const milestoneDone = (m: { status: string }) =>
  m.status === 'approved' || m.status === 'completed'

/** A deal counts once the content is live (or it has been paid) */
export const isCompletedDeal = (c: CollabLike) =>
  c.paymentStatus === 'paid' ||
  (c.milestones ?? []).some(
    (m) => (m.slug ?? m.type_slug) === 'collaboration_live' && milestoneDone(m),
  )

export const countCompletedDeals = (collabs: CollabLike[]) =>
  collabs.filter(isCompletedDeal).length

/** Accepted deals with a milestone past its due date that isn't done yet */
export const overdueDeals = <T extends CollabLike>(collabs: T[]): T[] => {
  const today = new Date().toISOString().slice(0, 10)
  return collabs.filter(
    (c) =>
      c.status === 'accepted' &&
      !isCompletedDeal(c) &&
      (c.milestones ?? []).some(
        (m) => !milestoneDone(m) && m.dueDate && m.dueDate.slice(0, 10) < today,
      ),
  )
}
